import React, { useEffect, useState } from 'react';
import client, { apiCall } from '../../api/client';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import Loader from '../../components/Loader';

const EMPTY_FILTERS = { status: '', network: '', fromDate: '', toDate: '' };
const PAGE_SIZE = 20;

export default function RpcLogs() {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [networks, setNetworks] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = (f = filters, pg = page) => {
    setLoading(true);
    const params = { page: String(pg) };
    if (f.status) params.status = f.status;
    if (f.network) params.network = f.network;
    if (f.fromDate) params.fromDate = f.fromDate;
    if (f.toDate) params.toDate = f.toDate;

    apiCall(client.get('/rpc_logs/admin/get_list', { params }))
      .then((data) => { setLogs(data.data); setTotalCount(data.totalCount); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { apiCall(client.get('/network/admin/get_list')).then(setNetworks).catch(() => setNetworks([])); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(filters, page), [page]);

  const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

  const handleApply = (e) => {
    e.preventDefault();
    setError('');
    setPage(1);
    load(filters, 1);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setError('');
    setPage(1);
    load(EMPTY_FILTERS, 1);
  };

  const columns = [
    { key: 'createdAt', label: 'Time', render: (row) => new Date(row.createdAt).toLocaleString() },
    { key: 'network', label: 'Network' },
    { key: 'method', label: 'Method', render: (row) => <code>{row.method}</code> },
    { key: 'apiKeyId', label: 'API Key', render: (row) => <code>{row.apiKeyId}</code> },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge value={row.status} labels={{ true: 'Success', false: 'Failed' }} /> },
    { key: 'httpStatus', label: 'HTTP' },
    { key: 'creditsCharged', label: 'Credits' },
    { key: 'latencyMs', label: 'Latency', render: (row) => `${row.latencyMs} ms` },
  ];

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasActiveFilters = filters.status || filters.network || filters.fromDate || filters.toDate;

  return (
    <div>
      <h1>RPC Logs</h1>
      {error && <div className="form-error">{error}</div>}

      <div className="card" style={{ marginBottom: 24 }}>
        <form onSubmit={handleApply} className="form-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ minWidth: 140 }}>
            <label>Status</label>
            <select name="status" value={filters.status} onChange={handleFilterChange}>
              <option value="">All</option>
              <option value="true">Success</option>
              <option value="false">Failed</option>
            </select>
          </div>
          <div className="form-group" style={{ minWidth: 160 }}>
            <label>Network</label>
            <select name="network" value={filters.network} onChange={handleFilterChange}>
              <option value="">All networks</option>
              {networks.map((n) => <option key={n.uniqueId} value={n.slug}>{n.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ minWidth: 150 }}>
            <label>From date</label>
            <input type="date" name="fromDate" value={filters.fromDate} onChange={handleFilterChange} />
          </div>
          <div className="form-group" style={{ minWidth: 150 }}>
            <label>To date</label>
            <input type="date" name="toDate" value={filters.toDate} onChange={handleFilterChange} />
          </div>
          <div className="form-group" style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary btn-sm">Apply</button>
            {hasActiveFilters && (
              <button type="button" className="btn btn-outline btn-sm" onClick={handleReset}>Reset</button>
            )}
          </div>
        </form>
      </div>

      <div className="card">
        {loading ? (
          <Loader label="Loading RPC logs..." />
        ) : (
          <>
            <DataTable columns={columns} rows={logs} emptyMessage="No RPC calls match these filters." />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
              <span style={{ alignSelf: 'center', fontSize: 13, color: 'var(--text-muted)' }}>Page {page} of {totalPages}</span>
              <button className="btn btn-outline btn-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
